// `configureSessionMergeOnboardingUi` — the injected-host seam for the
// session-merge onboarding cards, education modal and tab coachmark.
//
// Every member is an app value the moved UI renders or calls. The two hooks
// cross BY REFERENCE, so the rules of hooks apply through them unchanged. The
// three components are the app's shared presentational pieces, crossed whole
// because the components→design-system lane has not moved them yet.
//
// The app registers this once, from `src/composition/hosts/session/session-merge-onboarding-wiring.ts`,
// imported by `_app.tsx`. Tests rendering any of these surfaces configure a
// double first.
import { type ComponentType, type ReactNode } from 'react';

import { type ModalId } from '@heidi/schema/ml-scribe';

import { type AdaptiveLearningAwarenessCardProps } from '../adaptive-learning-popover/host';

/** The queue entry the education modal is enqueued with. */
export interface SessionMergeQueuedModal {
  id: ModalId;
  priority: number;
  dismissible: boolean;
}

/** Exactly the props the tab coachmark passes to the app coach-mark. */
export interface SessionMergeCoachMarkProps {
  anchorSelector: string;
  side?: 'top' | 'bottom' | 'left' | 'right';
  align?: 'start' | 'center' | 'end';
  icon?: ReactNode;
  title: ReactNode;
  description: ReactNode;
  image?: ReactNode;
  progress?: ReactNode;
  primaryLabel: ReactNode;
  onPrimary: () => void;
  onDismiss: () => void;
  'data-testid'?: string;
}

export interface SessionMergeOnboardingUiHost {
  /** `@/hooks/useQueuedModal`. */
  useQueuedModal: (modalId: ModalId) => {
    isOpen: boolean;
    dismissible: boolean;
    handleClose: () => void;
  };
  /** The setter half of the app queue's `enqueueModalAtom`. */
  useEnqueueModal: () => (modal: SessionMergeQueuedModal) => void;
  /** `@/models/analytics/features/useSessionMergeOnboardingAnalytics`. */
  useSessionMergeOnboardingAnalytics: () => {
    trackViewed: () => void;
    trackAccepted: () => void;
    trackDismissed: () => void;
  };
  /** `@heidi/snowflake-ui/coach-mark` `CoachMark`. */
  CoachMark: ComponentType<SessionMergeCoachMarkProps>;
  /** `@/components/awareness-card` `AwarenessCard`. */
  AwarenessCard: (props: AdaptiveLearningAwarenessCardProps) => ReactNode;
  /** `@/components/icons/session-merge-icon` `SessionMergeIcon`. */
  SessionMergeIcon: ComponentType<{ className?: string }>;
}

let host: SessionMergeOnboardingUiHost | null = null;

export const configureSessionMergeOnboardingUi = (
  next: SessionMergeOnboardingUiHost
): void => {
  host = next;
};

/** Fail loudly on unconfigured use — never silently degrade. */
export const useSessionMergeOnboardingUiHost = (): SessionMergeOnboardingUiHost => {
  if (!host) {
    throw new Error(
      '[session/ui/session-merge-onboarding] not configured — the app must call ' +
        'configureSessionMergeOnboardingUi() at boot (see ' +
        'src/composition/hosts/session/session-merge-onboarding-wiring.ts, imported by _app.tsx); ' +
        'tests rendering these surfaces must configure a test double first.'
    );
  }
  return host;
};

/** Test-only: drops the registered host so a suite can assert the failure. */
export const __resetSessionMergeOnboardingUiHostForTests = (): void => {
  host = null;
};
